import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../App";
import CarCard from "../components/CarCard";
import SearchBox from "../components/SearchBox";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { SlidersHorizontal, X, Car, Loader2, Map as MapIcon, List } from "lucide-react";
import SearchMap from "../components/SearchMap";
import useSeo from "../hooks/useSeo";

// Sensible floors for the "Precio por día" slider. The actual ceiling is
// driven by the live catalog (via `/api/vehicles/stats/price-range`) so
// the slider auto-adapts to what hosts have listed. `MIN_SLIDER_CEILING`
// prevents a 0-width slider when a city has only 1–2 cheap listings,
// and `MAX_SLIDER_CEILING` keeps the range readable if a single luxury
// listing pushes the top price into 6-figure territory.
const MIN_SLIDER_CEILING = 500;
const MAX_SLIDER_CEILING = 100000;

export default function SearchResultsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const city = searchParams.get("city") || "Tijuana";
  useSeo({
    title: `Renta de autos en ${city} · Rivones`,
    description: `Encuentra los mejores autos en renta en ${city}. Renta de autos entre particulares — reserva desde tu celular, sin papeleo.`,
    keywords: `renta de autos en ${city}, renta de autos, alquiler de autos ${city}, rentar auto en ${city}, renta de autos entre particulares`,
    canonical: `https://rivones.com/search?city=${encodeURIComponent(city)}`,
  });
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    city: searchParams.get("city") || "Tijuana",
    minPrice: 0,
    maxPrice: MAX_SLIDER_CEILING,
    transmission: "",
    seats: "",
    delivery: false,
    unlimitedMileage: false,
    instantBook: false,
  });

  // Dynamic slider ceiling — driven by `/api/vehicles/stats/price-range`
  // for the current city. Starts at the safe floor so the slider is
  // usable before the request resolves.
  const [sliderMax, setSliderMax] = useState(MIN_SLIDER_CEILING);
  const [priceRange, setPriceRange] = useState([0, MIN_SLIDER_CEILING]);
  const [priceRangeUserTouched, setPriceRangeUserTouched] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [userLocation, setUserLocation] = useState(null); // {lat, lng} when "near me" used
  // Mobile map/list view is driven by the ?view=map URL param so the BottomNav Mapa
  // tab and the in-page Mapa/List toggle stay in sync.
  const mobileView = searchParams.get("view") === "map" ? "map" : "list";
  const setMobileView = (next) => {
    const newParams = new URLSearchParams(searchParams);
    if (next === "map") newParams.set("view", "map");
    else newParams.delete("view");
    setSearchParams(newParams, { replace: true });
  };

  // Whenever the selected city changes, refresh the price-range stats so
  // the slider ceiling reflects what's actually listed there. We floor at
  // MIN_SLIDER_CEILING to avoid a 0-width slider for cities with a single
  // cheap listing, and cap at MAX_SLIDER_CEILING to keep the range
  // readable if a $500k/day outlier ever slips in.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/vehicles/stats/price-range", {
          params: { city: filters.city },
        });
        if (cancelled) return;
        const top = Math.max(
          MIN_SLIDER_CEILING,
          Math.min(MAX_SLIDER_CEILING, Math.ceil((data.max || 0) / 100) * 100),
        );
        setSliderMax(top);
        // If the user hasn't touched the slider yet, keep it wide open
        // (0…top). If they have, don't clobber their selection unless the
        // catalog no longer reaches that ceiling.
        setPriceRange((prev) => {
          if (!priceRangeUserTouched) return [0, top];
          return [prev[0], Math.min(prev[1], top)];
        });
        setFilters((f) => ({
          ...f,
          maxPrice: priceRangeUserTouched ? Math.min(f.maxPrice, top) : top,
        }));
      } catch (_e) {
        // Silent: keep the safe floor and let the user filter manually.
      }
    })();
    return () => { cancelled = true; };
  }, [filters.city]);

  useEffect(() => {
    fetchVehicles();
    // Also re-fetch when the URL ?category param changes so category
    // chips navigating within /search actually update the results.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchParams]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("city", filters.city);
      // Category filter — driven by /search?category=suv links from the
      // home page category grid. Passed straight through to the backend
      // which supports the full VEHICLE_CATEGORIES enum.
      const cat = searchParams.get("category");
      if (cat) params.set("category", cat);
      if (filters.minPrice > 0) params.set("min_price", filters.minPrice);
      if (filters.maxPrice < sliderMax) params.set("max_price", filters.maxPrice);
      if (filters.transmission) params.set("transmission", filters.transmission);
      if (filters.seats) params.set("seats", filters.seats);
      if (filters.delivery) params.set("delivery", "true");
      if (filters.unlimitedMileage) params.set("unlimited_mileage", "true");
      if (filters.instantBook) params.set("instant_book", "true");

      const response = await api.get(`/vehicles?${params.toString()}`);
      setVehicles(response.data.vehicles || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (value) => {
    setPriceRange(value);
    setPriceRangeUserTouched(true);
  };

  // Vehicles sorted by distance to user when "Find near me" was used
  const sortedVehicles = useMemo(() => {
    if (!userLocation || vehicles.length === 0) return vehicles;
    const { lat: uLat, lng: uLng } = userLocation;
    const toRad = (d) => (d * Math.PI) / 180;
    const distKm = (lat, lng) => {
      const R = 6371;
      const dLat = toRad(lat - uLat);
      const dLng = toRad(lng - uLng);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(uLat)) * Math.cos(toRad(lat)) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(a));
    };
    return [...vehicles]
      .map((v) => ({
        ...v,
        _distance_km:
          typeof v.location_lat === "number" && typeof v.location_lng === "number"
            ? distKm(v.location_lat, v.location_lng)
            : Number.POSITIVE_INFINITY,
      }))
      .sort((a, b) => a._distance_km - b._distance_km);
  }, [vehicles, userLocation]);

  const applyPriceFilter = () => {
    setFilters((prev) => ({
      ...prev,
      minPrice: priceRange[0],
      maxPrice: priceRange[1],
    }));
  };

  const clearFilters = () => {
    setFilters({
      city: "Tijuana",
      minPrice: 0,
      maxPrice: sliderMax,
      transmission: "",
      seats: "",
      delivery: false,
      unlimitedMileage: false,
      instantBook: false,
    });
    setPriceRange([0, sliderMax]);
    setPriceRangeUserTouched(false);
  };

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Price Range */}
      <div>
        <Label className="text-sm font-semibold">{t("searchResults.pricePerDay")}</Label>
        <div className="mt-4 px-2">
          <Slider
            value={priceRange}
            onValueChange={handlePriceChange}
            onValueCommit={applyPriceFilter}
            min={0}
            max={sliderMax}
            step={sliderMax > 5000 ? 100 : 50}
            className="mb-2"
          />
          <div className="flex justify-between text-sm text-gray-600">
            <span>${priceRange[0].toLocaleString("es-MX")}</span>
            <span>${priceRange[1].toLocaleString("es-MX")}</span>
          </div>
        </div>
      </div>

      {/* Transmission */}
      <div>
        <Label className="text-sm font-semibold">{t("searchResults.transmission")}</Label>
        <Select
          value={filters.transmission || "all"}
          onValueChange={(value) => setFilters((prev) => ({ ...prev, transmission: value === "all" ? "" : value }))}
        >
          <SelectTrigger className="mt-2">
            <SelectValue placeholder={t("searchResults.any")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("searchResults.any")}</SelectItem>
            <SelectItem value="automatic">{t("searchResults.automatic")}</SelectItem>
            <SelectItem value="manual">{t("searchResults.manual")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Seats */}
      <div>
        <Label className="text-sm font-semibold">{t("searchResults.minSeats")}</Label>
        <Select
          value={filters.seats || "all"}
          onValueChange={(value) => setFilters((prev) => ({ ...prev, seats: value === "all" ? "" : value }))}
        >
          <SelectTrigger className="mt-2">
            <SelectValue placeholder={t("searchResults.any")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("searchResults.any")}</SelectItem>
            <SelectItem value="2">2+</SelectItem>
            <SelectItem value="4">4+</SelectItem>
            <SelectItem value="5">5+</SelectItem>
            <SelectItem value="7">7+</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Checkboxes */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Checkbox
            id="delivery"
            checked={filters.delivery}
            onCheckedChange={(checked) => setFilters((prev) => ({ ...prev, delivery: checked }))}
          />
          <Label htmlFor="delivery" className="text-sm cursor-pointer">
            {t("searchResults.homeDelivery")}
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="unlimited"
            checked={filters.unlimitedMileage}
            onCheckedChange={(checked) => setFilters((prev) => ({ ...prev, unlimitedMileage: checked }))}
          />
          <Label htmlFor="unlimited" className="text-sm cursor-pointer">
            {t("searchResults.unlimitedMileage")}
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="instant"
            checked={filters.instantBook}
            onCheckedChange={(checked) => setFilters((prev) => ({ ...prev, instantBook: checked }))}
          />
          <Label htmlFor="instant" className="text-sm cursor-pointer">
            {t("searchResults.instantBook")}
          </Label>
        </div>
      </div>

      {/* Clear Filters */}
      <Button
        variant="outline"
        className="w-full"
        onClick={clearFilters}
        data-testid="clear-filters-btn"
      >
        {t("searchResults.clearFilters")}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-sand" data-testid="search-results-page">
      {/* Search Box */}
      <div className="bg-white border-b border-[#E5E0D8] py-4">
        <div className="container-main">
          <SearchBox
            className="shadow-none p-0"
            initialValues={{ location: filters.city }}
          />
        </div>
      </div>

      <div className="container-main py-6">
        <div className="flex gap-6">
          {/* Filters Sidebar - Desktop */}
          <aside className="hidden lg:block w-60 flex-shrink-0">
            <div className="card-base sticky top-24">
              <h3 className="font-heading font-semibold text-lg mb-6">{t("searchResults.filters")}</h3>
              <FilterContent />
            </div>
          </aside>

          {/* Main Content - List */}
          <main className={`flex-1 min-w-0 ${mobileView === "map" ? "hidden lg:block" : ""}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6 gap-2">
              <div className="min-w-0">
                <h1 className="text-2xl font-heading font-bold text-agave truncate">
                  {t("searchResults.title", { city: filters.city })}
                </h1>
                <p className="text-gray-600 text-sm">
                  {loading ? t("searchResults.searching") : t("searchResults.available", { count: total })}
                </p>
              </div>

              <div className="flex items-center gap-2">
                {/* Mobile View Toggle */}
                <Button
                  variant="outline"
                  className="lg:hidden"
                  onClick={() => setMobileView("map")}
                  data-testid="mobile-view-map-btn"
                >
                  <MapIcon className="w-4 h-4 mr-2" />
                  {t("searchResults.map")}
                </Button>
                {/* Mobile Filters Button */}
                <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden" data-testid="mobile-filters-btn">
                      <SlidersHorizontal className="w-4 h-4" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-80">
                    <h3 className="font-heading font-semibold text-lg mb-6">{t("searchResults.filters")}</h3>
                    <FilterContent />
                  </SheetContent>
                </Sheet>
              </div>
            </div>

            {/* Results Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
              </div>
            ) : sortedVehicles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {sortedVehicles.map((vehicle) => (
                  <div
                    key={vehicle.vehicle_id}
                    onMouseEnter={() => setHoveredId(vehicle.vehicle_id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    <CarCard vehicle={vehicle} distanceKm={vehicle._distance_km} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {t("searchResults.noCarsTitle")}
                </h3>
                <p className="text-gray-500 mb-4">
                  {t("searchResults.noCarsBody")}
                </p>
                <Button onClick={clearFilters} className="btn-primary">
                  {t("searchResults.clearFilters")}
                </Button>
              </div>
            )}
          </main>

          {/* Map - Desktop sticky right column / Mobile fullscreen */}
          <aside
            className={`${
              mobileView === "map"
                ? "block fixed inset-0 z-[60] bg-white p-4 pt-20 pb-24 flex flex-col lg:relative lg:inset-auto lg:z-auto lg:p-0 lg:pt-0 lg:pb-0 lg:block"
                : "hidden lg:block"
            } lg:w-[420px] xl:w-[500px] flex-shrink-0`}
          >
            <div className={`${mobileView === "map" ? "flex flex-col flex-1 min-h-0" : ""} lg:block lg:sticky lg:top-24`}>
              {mobileView === "map" && (
                <Button
                  variant="outline"
                  className="lg:hidden mb-3 w-full flex-shrink-0"
                  onClick={() => setMobileView("list")}
                  data-testid="mobile-view-list-btn"
                >
                  <List className="w-4 h-4 mr-2" />
                  Volver a la lista
                </Button>
              )}
              <SearchMap
                vehicles={sortedVehicles}
                hoveredId={hoveredId}
                onUserLocation={setUserLocation}
                className={`${mobileView === "map" ? "flex-1 min-h-0" : "h-[calc(100vh-160px)] min-h-[400px]"} lg:h-[calc(100vh-140px)] lg:flex-none lg:min-h-[400px]`}
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
